import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { usePostDocumentsMutation } from "src/core/api/documents";
import { useFolder, useFolderCorporationId } from "src/core/contexts/FolderContext";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import {
  defaultEntrepriseFormValues,
  entrepriseSchema,
} from "src/core/forms/folder/entreprise.schema";
import { computeCorporationIncompleteFields } from "src/core/forms/folder/folder.incomplete";
import {
  DataType,
  useCreateOrReplaceExternalDocumentMutation,
  useDeleteDocumentMutation,
  useDeleteExternalDocumentMutation,
  useSearchCustomerFolderByStringLazyQuery,
  useUpdateCorporationMutation,
  useUpdateDocumentMutation,
} from "src/core/gql/graphql";
import { useLegalStatusOptions } from "src/core/hooks/common/useLegalStatusOptions";
import { useRequiredDocuments } from "src/core/hooks/common/useRequiredDocuments";
import { useEReputation } from "src/core/hooks/folder/useEReputation";
import { useUploadDocumentsErrorToast } from "src/core/hooks/folder/useUploadDocumentsErrorToast";
import { useToast } from "src/core/hooks/use-toast";
import { ICustomDuplicateFolder } from "src/core/interfaces/Folders";
import { getDefaultDocuments } from "src/core/mappers/document.mapper";
import {
  getFolderFieldValue,
  mapFolderFilesToDuplicateFolders,
} from "src/core/mappers/folder.mapper";
import { deleteDocuments, uploadDocuments } from "src/core/services/document.service";
import { Document } from "src/core/types/document";
import { FolderEntrepriseFormValues } from "src/core/types/folder";
import { toIsoUtcFromDate } from "src/core/utils/date";
import { getDocumentsExpirationUpdates } from "src/core/utils/documents/getDocumentsExpirationUpdates";
import { buildCorporationInputValues } from "src/core/utils/folder";
import {
  calculateCompanyMissingDocuments,
  calculateCompanyMissingFields,
} from "src/core/utils/folderAnalysis";

export const useFolderEntrepriseForm = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { id: folderId } = useParams();

  const { folderFile, refetch, isReadOnly } = useFolder();
  const { countryWithRiskOptions, activityWithRiskOptions, legalTypesWithRiskOptions } =
    useRiskOptions();
  const missingInfo = calculateCompanyMissingFields(folderFile);
  const missingDocuments = calculateCompanyMissingDocuments(folderFile);
  const corporationId = useFolderCorporationId();

  const { legalStatusOptions, loading: legalStatusOptionsLoading } = useLegalStatusOptions();

  const requiredDocuments = useRequiredDocuments(folderFile, corporationId);
  const { handleUploadErrors } = useUploadDocumentsErrorToast({ requiredDocuments, toast });

  const { callSafely: callEReputationSafely } = useEReputation();

  const [updateCorporation] = useUpdateCorporationMutation();
  const prevTypeJuridiqueRef = useRef<string | undefined>(
    getFolderFieldValue(folderFile, "entityTypeCode", "unlisted")
  );
  const [searchBySiren] = useSearchCustomerFolderByStringLazyQuery();
  const [duplicateFolder, setDuplicateFolder] = useState<ICustomDuplicateFolder | undefined>();
  const [postDocuments] = usePostDocumentsMutation();
  const [createOrReplaceExternalDocument] = useCreateOrReplaceExternalDocumentMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [deleteExternalDocument] = useDeleteExternalDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [defaultDocuments, setDefaultDocuments] = useState<Record<string, Document>>({});
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Liste des champs requis pour la validation du dossier, mais pas nécessaire pour l'enregistrement.
  // On les affiche dans un état différent des champs requis pour l'enregistrement.
  const [incompleteFields, setIncompleteFields] = useState<Set<string>>(new Set());

  const form = useForm<FolderEntrepriseFormValues>({
    resolver: zodResolver(entrepriseSchema({ t })),
    defaultValues: defaultEntrepriseFormValues(folderFile),
    mode: "onSubmit",
  });
  useEffect(() => {
    if (!corporationId) return;

    const defaultDoc = getDefaultDocuments(folderFile, corporationId, requiredDocuments);
    setDefaultDocuments(defaultDoc);

    form.reset({
      ...form.getValues(),
      documents: defaultDoc,
    });
  }, [requiredDocuments, folderFile, corporationId, form]);

  const typeJuridique = form.watch("typeJuridique");

  // useEffect séparé pour gérer le calcul des champs incomplets après que les documents soient chargés
  useEffect(() => {
    if (Object.keys(defaultDocuments).length > 0) {
      // Utiliser les valeurs actuelles du formulaire qui incluent les documents mis à jour
      const currentFormValues = form.getValues();
      setIncompleteFields(computeCorporationIncompleteFields(currentFormValues));
    }
  }, [defaultDocuments, form]);

  useEffect(() => {
    if (typeJuridique && corporationId && typeJuridique !== prevTypeJuridiqueRef.current) {
      setIsLoading(true);
      updateCorporation({
        variables: {
          corporationId,
          inputValues: [
            { fieldCode: "entityTypeCode", value: typeJuridique, dataType: DataType.Code },
          ],
        },
      })
        .then(async () => {
          await refetch();
          setIncompleteFields(new Set());
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    prevTypeJuridiqueRef.current = typeJuridique;
  }, [typeJuridique, corporationId, folderId, updateCorporation, refetch]);

  const formeJuridique = form.watch("formeJuridique");
  const showFormeJuridiquePrecision = formeJuridique === "other";

  const onSubmit = async (data: FolderEntrepriseFormValues) => {
    //vérifier si le siren existe
    if (data.siren) {
      const { data: clientData } = await searchBySiren({
        variables: {
          searchString: data.siren,
        },
      });

      const totalCount = clientData?.customerFoldersPage?.pageInfo?.totalCount ?? 0;
      if (totalCount > 0 && clientData) {
        const duplicateFolders = mapFolderFilesToDuplicateFolders(clientData);
        if (
          duplicateFolders.length > 0 &&
          duplicateFolders[0].id !== folderId &&
          duplicateFolders[0].siren === data.siren
        ) {
          setDuplicateFolder(duplicateFolders[0]);
          form.setError("siren", {
            type: "duplicate",
            message: t("form.createClient.error.sirenAlreadyExists"),
          });
          return;
        }
      }
    }

    if (corporationId && folderId) {
      try {
        setIsLoading(true);

        const corporationData = buildCorporationInputValues(data);

        // Détecter les documents de type "kbis" dont l'expirationDate a changé
        const kbisDocumentsToUpdate = getDocumentsExpirationUpdates(
          data.documents,
          defaultDocuments,
          { includeDocCode: (docCode) => docCode === "kbis" }
        );

        // 1) On lance les opérations “métier” (hors upload)
        await Promise.all([
          updateCorporation({
            variables: { corporationId, inputValues: corporationData },
          }),
          deleteDocuments(data.documents, defaultDocuments, deleteDocument, deleteExternalDocument),
          ...kbisDocumentsToUpdate.map(({ fileId, expirationDate }) =>
            updateDocument({
              variables: {
                id: fileId,
                expirationDate: expirationDate
                  ? toIsoUtcFromDate(expirationDate, "yyyy-MM-dd")
                  : undefined,
              },
            })
          ),
        ]);

        // 2) Upload des documents (résultats par document)
        const uploadResults = await uploadDocuments(
          data.documents,
          corporationId,
          postDocuments,
          createOrReplaceExternalDocument
        );

        const failedUploads = uploadResults.filter((r) => !r.ok);

        // Affichage d'un toast d'erreur si des documents n'ont pas pu être uploadés
        handleUploadErrors(failedUploads);

        // 3) Appel à eReputation pour que le screening se fasse après la mise à jour de l'entreprise.
        await callEReputationSafely({
          folderFileId: folderId,
          logContext: "useFolderEntrepriseForm::onSubmit",
        });

        await refetch();

        // Gestion du cas des champs requis pour la validation du dossier, mais pas nécessaire pour l'enregistrement.
        setIncompleteFields(computeCorporationIncompleteFields(data));
        onSuccess?.();
        setMessage({ type: "success", text: t("form.save.success") });
        toast({
          description: t("global.form.success"),
        });
      } catch (error) {
        setMessage({ type: "error", text: t("form.save.error") });
        toast({
          description: t("global.form.fail"),
          variant: "destructive",
        });
        console.error("Erreur lors de la sauvegarde:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return {
    form,
    countryWithRiskOptions,
    activityWithRiskOptions,
    legalTypesWithRiskOptions,
    legalStatusOptions,
    legalStatusOptionsLoading,
    requiredDocuments,
    showFormeJuridiquePrecision,
    onSubmit,
    isLoading: isLoading,
    duplicateFolder,
    message,
    missingInfo,
    missingDocuments,
    isReadOnly,
    incompleteFields,
  };
};
